<template>
  <Teleport to="body">
  <div v-if="isVisible" class="fixed inset-0 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm z-50 p-0 sm:p-4" @click.self="closePopup">
    <div class="bg-white w-full sm:w-[480px] sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[90vh]">

      <!-- Header -->
      <div class="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
        <div>
          <h3 class="text-base font-semibold text-gray-900">{{ t('passengers.title') }}</h3>
          <p class="text-xs text-gray-400 mt-0.5">{{ t('passengers.handLuggage') }}</p>
        </div>
        <button type="button" @click="closePopup" class="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Cabin class row -->
      <div class="px-5 py-3 flex items-center justify-between border-b border-gray-100">
        <span class="text-sm font-medium text-gray-700">{{ t('passengers.cabinClass') }}</span>
        <div class="relative">
          <select
            id="global-class"
            v-model="globalClass"
            class="appearance-none border border-gray-200 rounded-lg py-1.5 pl-3 pr-8 text-sm bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="economy">{{ t('passengers.economy') }}</option>
            <option value="premium_economy">{{ t('passengers.premiumEconomy') }}</option>
            <option value="business">{{ t('passengers.business') }}</option>
            <option value="first_class">{{ t('passengers.firstClass') }}</option>
          </select>
          <div class="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-500">
            <svg class="fill-current h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M5.516 7.548a.75.75 0 011.06 0L10 10.918l3.424-3.37a.75.75 0 111.06 1.06l-4.062 3.99a.75.75 0 01-1.06 0L5.516 8.608a.75.75 0 010-1.06z" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Passenger List -->
      <div class="overflow-y-auto flex-grow px-5 py-3 space-y-3" ref="scrollContainer">
        <div
          v-for="(passenger, pIndex) in passengerDetails"
          :key="passenger.id"
          class="border border-gray-200 rounded-xl overflow-hidden"
        >
          <!-- Passenger row -->
          <div class="flex items-center px-4 py-3 bg-gray-50">
            <span class="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold mr-3 flex-shrink-0">
              {{ pIndex + 1 }}
            </span>
            <span class="text-sm font-medium text-gray-800 flex-grow">{{ t('passengers.passenger') }}{{ pIndex + 1 }}</span>

            <!-- No bags toggle -->
            <button
              type="button"
              @click="passenger.noBags = !passenger.noBags; toggleNoBags(pIndex)"
              :class="passenger.noBags
                ? 'bg-gray-200 text-gray-500'
                : 'bg-blue-50 text-blue-600 border border-blue-200'"
              class="text-xs font-medium px-2.5 py-1 rounded-full transition-colors mr-2"
            >
              {{ passenger.noBags ? t('passengers.noBags') : t('passengers.plusBags') }}
            </button>

            <!-- Remove passenger -->
            <button type="button"
              v-if="passengerDetails.length > 1"
              @click="removePassenger(pIndex)"
              class="p-1 text-gray-300 hover:text-red-400 transition-colors rounded-full"
              :aria-label="t('searchForm.removePassenger')"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Bags -->
          <div v-if="!passenger.noBags" class="px-4 py-3 space-y-2 bg-white">
            <div
              v-for="(bag, bIndex) in passenger.bags"
              :key="bIndex"
              class="flex items-center gap-2"
            >
              <!-- Bag type -->
              <div class="relative flex-grow">
                <select
                  v-model="bag.type"
                  class="appearance-none w-full border border-gray-200 rounded-lg py-1.5 pl-3 pr-7 text-sm bg-white text-gray-700 focus:ring-2 focus:ring-blue-500"
                >
                  <option v-for="type in getAvailableBagTypes(pIndex, bIndex)" :key="type" :value="type">
                    {{ formatBagType(type) }}
                  </option>
                </select>
                <div class="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-400">
                  <svg class="fill-current h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M5.516 7.548a.75.75 0 011.06 0L10 10.918l3.424-3.37a.75.75 0 111.06 1.06l-4.062 3.99a.75.75 0 01-1.06 0L5.516 8.608a.75.75 0 010-1.06z" />
                  </svg>
                </div>
              </div>
              <!-- Quantity -->
              <div class="relative w-16 flex-shrink-0">
                <select
                  v-model.number="bag.numberOfBags"
                  class="appearance-none w-full border border-gray-200 rounded-lg py-1.5 pl-3 pr-6 text-sm bg-white text-gray-700 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1">×1</option>
                  <option value="2">×2</option>
                </select>
                <div class="pointer-events-none absolute inset-y-0 right-1 flex items-center text-gray-400">
                  <svg class="fill-current h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M5.516 7.548a.75.75 0 011.06 0L10 10.918l3.424-3.37a.75.75 0 111.06 1.06l-4.062 3.99a.75.75 0 01-1.06 0L5.516 8.608a.75.75 0 010-1.06z" />
                  </svg>
                </div>
              </div>
              <!-- Remove bag -->
              <button type="button" @click="removeBag(pIndex, bIndex)" class="p-1 text-gray-300 hover:text-red-400 transition-colors flex-shrink-0" aria-label="Remove Bag">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Add bag -->
            <button type="button"
              v-if="passenger.bags.length < 2"
              @click="addBag(pIndex)"
              class="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 mt-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
              </svg>
              {{ t('passengers.addCheckedBag') }}
            </button>
          </div>
        </div>

        <!-- Add passenger -->
        <button
          v-if="canAddPassenger"
          type="button"
          @click="addPassenger"
          class="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-500 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-colors"
        >
          <UserPlusIcon class="h-4 w-4" />
          {{ t('passengers.addPassenger') }}
        </button>
      </div>

      <!-- Footer: summary + actions -->
      <div class="px-5 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
        <!-- Summary line -->
        <p class="text-xs text-gray-500 mb-3">
          <span v-for="(group, i) in passengerGroups" :key="group.key">
            <span v-if="i > 0"> · </span>{{ group.count }}× {{ group.bags }}
          </span>
        </p>
        <div class="flex gap-3">
          <button type="button"
            @click="resetPassengers"
            class="py-2.5 px-4 border border-gray-200 text-sm font-medium rounded-xl text-gray-500 bg-white hover:bg-gray-50 transition-colors"
          >
            {{ t('passengers.reset') }}
          </button>
          <button type="button"
            @click="closePopup"
            class="flex-1 py-2.5 border border-gray-200 text-sm font-medium rounded-xl text-gray-600 bg-white hover:bg-gray-50 transition-colors"
          >
            {{ t('searchForm.cancel') }}
          </button>
          <button type="button"
            @click="saveAndClose"
            class="flex-1 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
          >
            {{ t('searchForm.done') }}
          </button>
        </div>
      </div>

    </div>
  </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { UserPlusIcon } from '@heroicons/vue/24/outline';

const { t } = useI18n();

// Define props to receive passenger details, visibility state, and initial global class
const props = defineProps({
  isVisible: {
    type: Boolean,
    required: true
  },
  initialPassengerDetails: {
    type: Array,
    required: true,
    default: () => [] // Ensure default is an empty array
  },
  initialGlobalClass: { // New prop for initial global class
    type: String,
    required: false,
    default: 'economy'
  }
});

// Define emits for closing the popup and updating passenger details
const emit = defineEmits(['close', 'update-passenger-details']);

// Generate unique IDs for passengers and bags
const generateId = () => '_' + Math.random().toString(36).substr(2, 9);

// Function to safely retrieve bags, defaulting to an empty array if undefined
const getSafeBags = (bags) => {
  return Array.isArray(bags) ? bags : [];
};

// Local copy of passenger details to allow editing
const passengerDetails = ref(
  props.initialPassengerDetails.map(passenger => ({
    ...passenger,
    id: passenger.id || generateId(),
    noBags: getSafeBags(passenger.bags).length === 0, // Initialize noBags based on existing bags
    bags: getSafeBags(passenger.bags).map(bag => ({
      type: bag.type || 'cabin_bag',
      numberOfBags: bag.numberOfBags || 1
    }))
  }))
);

// Reactive variable for global class selection
const globalClass = ref(props.initialGlobalClass);

// Helper: sync local state from props
const syncFromProps = (passengers) => {
  if (!Array.isArray(passengers)) return;
  passengerDetails.value.splice(
    0,
    passengerDetails.value.length,
    ...passengers.map(passenger => ({
      ...passenger,
      id: passenger.id || generateId(),
      noBags: getSafeBags(passenger.bags).length === 0,
      bags: getSafeBags(passenger.bags).map(bag => ({
        type: bag.type || 'cabin_bag',
        numberOfBags: bag.numberOfBags || 1
      }))
    }))
  );
  globalClass.value = props.initialGlobalClass || 'economy';
};

// Reset state every time the popup opens so previous edits don't bleed through
watch(
  () => props.isVisible,
  (visible) => {
    if (visible) syncFromProps(props.initialPassengerDetails);
  }
);

// Also react to prop changes while open
watch(
  () => props.initialPassengerDetails,
  (newVal) => syncFromProps(newVal),
  { deep: true }
);

// Watch for changes in initialGlobalClass to update globalClass
watch(
  () => props.initialGlobalClass,
  (newClass) => {
    globalClass.value = newClass || 'economy';
  }
);

// Define the maximum number of passengers
const MAX_PASSENGERS = 9;

// Computed property to determine if a new passenger can be added
const canAddPassenger = computed(() => passengerDetails.value.length < MAX_PASSENGERS);

// Method to close the popup without saving
const closePopup = () => {
  emit('close');
};

// Reset to a single passenger with no bags
const resetPassengers = () => {
  passengerDetails.value = [{ id: generateId(), bags: [], noBags: false }];
  globalClass.value = 'economy';
};

// Method to save changes and close the popup
const saveAndClose = () => {
  if (!Array.isArray(passengerDetails.value)) {
    console.error('passengerDetails should be an array:', passengerDetails.value);
    return;
  }

  const cleanedDetails = passengerDetails.value.map(({ noBags, bags, ...rest }) => {
    if (!Array.isArray(bags)) {
      console.error('bags should be an array for passenger:', rest);
      return {
        ...rest,
        noBags,
        bags: [] // Default to empty array if bags is not an array
      };
    }

    return {
      ...rest,
      noBags,
      bags: bags.map(bag => {
        if (typeof bag !== 'object' || bag === null) {
          console.error('Invalid bag object:', bag);
          return {}; // Default to empty object if bag is invalid
        }
        return { ...bag };
      })
    };
  });

  emit('update-passenger-details', {
    globalClass: globalClass.value,
    passengers: cleanedDetails
  });
  emit('close');
};

// Computed Property to Generate the Summary String with Grouped Passengers
const passengerGroups = computed(() => {
  const groups = {};

  passengerDetails.value.forEach(passenger => {
    // Generate a unique key based on bag configuration
    let key = '';

    if (passenger.noBags) {
      key = t('passengers.noBags');
    } else {
      // Sort bags by type to ensure consistent grouping
      const sortedBags = [...passenger.bags].sort((a, b) => a.type.localeCompare(b.type));
      key = sortedBags.map(bag => `${formatBagType(bag.type)} (${bag.numberOfBags})`).join(', ');
    }

    if (groups[key]) {
      groups[key].count += 1;
    } else {
      groups[key] = {
        count: 1,
        bags: key
      };
    }
  });

  // Convert groups object to an array
  return Object.keys(groups).map(key => ({
    key,
    count: groups[key].count,
    bags: groups[key].bags
  }));
});

// Helper Methods to Format Bag Types
const formatBagType = (bag) => {
  switch(bag) {
    case 'cabin_bag':
      return t('searchForm.cabinBag');
    case 'checked_bag':
      return t('searchForm.checkedBag');
    default:
      return bag;
  }
};

// Method to Add a New Passenger at the Bottom
const addPassenger = () => {
  if (!canAddPassenger.value) {
    // Optionally, display a notification or alert
    alert(`You can add a maximum of ${MAX_PASSENGERS} passengers.`);
    return;
  }

  passengerDetails.value.push({
    id: generateId(),
    noBags: true,
    bags: []
  });

  nextTick(() => {
    if (scrollContainer.value) {
      scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
    }
  });
};

// Method to Remove a Passenger
const removePassenger = (pIndex) => {
  if (passengerDetails.value.length > 1) {
    // Remove the passenger
    passengerDetails.value.splice(pIndex, 1);
  } else {
    // If only one passenger, toggle the noBags checkbox
    toggleNoBags(pIndex);
  }
};

// Method to Add a Bag Entry for a Passenger
const addBag = (pIndex) => {
  const passenger = passengerDetails.value[pIndex];
  if (passenger.bags.length < 2) {
    if (passenger.noBags) {
      // If No Bags is checked, uncheck it
      passenger.noBags = false;
    }
    // Determine available bag types to prevent duplicates
    const selectedTypes = passenger.bags.map(bag => bag.type);
    const availableTypes = ['cabin_bag', 'checked_bag'].filter(type => !selectedTypes.includes(type));
    if (availableTypes.length > 0) {
      passenger.bags.push({
        type: availableTypes[0],
        numberOfBags: 1
      });
    }
  }
};

// Method to Remove a Bag Entry for a Passenger
const removeBag = (pIndex, bIndex) => {
  const passenger = passengerDetails.value[pIndex];
  if (passenger.bags.length > 0) {
    passenger.bags.splice(bIndex, 1);
    // If all bags are removed, set noBags to true
    if (passenger.bags.length === 0) {
      passenger.noBags = true;
    }
  }
};

// Method to Toggle "No Bags" Checkbox
const toggleNoBags = (pIndex) => {
  const passenger = passengerDetails.value[pIndex];
  if (passenger.noBags) {
    // If "No Bags" is checked, clear all bags
    passenger.bags = [];
  } else {
    // If "No Bags" is unchecked, add a default bag if none exist
    if (passenger.bags.length === 0) {
      passenger.bags.push({
        type: 'cabin_bag',
        numberOfBags: 1
      });
    }
  }
};

// Method to Get Available Bag Types for a Specific Bag
const getAvailableBagTypes = (pIndex, bIndex) => {
  const passenger = passengerDetails.value[pIndex];
  // Get types selected by other bags
  const selectedTypes = passenger.bags
    .filter((_, index) => index !== bIndex)
    .map(bag => bag.type);
  // Return types that are not selected by other bags
  return ['cabin_bag', 'checked_bag'].filter(type => !selectedTypes.includes(type));
};

// Reference to the scrollable container to determine if scrolling is needed
const scrollContainer = ref(null);
const isScrollable = ref(false);
const scrollTop = ref(0);
const scrollHeight = ref(0);
const clientHeight = ref(0);

// Function to check if the container is scrollable
const checkScroll = () => {
  if (scrollContainer.value) {
    scrollHeight.value = scrollContainer.value.scrollHeight;
    clientHeight.value = scrollContainer.value.clientHeight;
    isScrollable.value = scrollContainer.value.scrollHeight > scrollContainer.value.clientHeight;
  }
};

// Watch for scroll events to manage shadows
const onScroll = () => {
  if (scrollContainer.value) {
    scrollTop.value = scrollContainer.value.scrollTop;
  }
};

// Watch for changes that might affect scrollability
watch(
  () => passengerDetails.value.length,
  () => {
    // Wait for DOM updates
    nextTick(() => {
      checkScroll();
    });
  }
);

// Check scroll on component mount and window resize
onMounted(() => {
  checkScroll();
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', onScroll);
  }
  window.addEventListener('resize', checkScroll);

  console.log('Updating passenger details:', props.initialPassengerDetails);

});
onUnmounted(() => {
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', onScroll);
  }
  window.removeEventListener('resize', checkScroll);
});
</script>

<style scoped>
/* Scroll Container Shadows */
.relative {
  position: relative;
}

.absolute {
  position: absolute;
}

/* Ensure Remove Bag button is centered vertically */
.flex.items-center.justify-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Optional: Add smooth transitions for shadows */
.absolute.bg-gradient-to-b,
.absolute.bg-gradient-to-t {
  transition: opacity 0.3s;
}

.passenger-form-section {
  /* Using Tailwind's utility for inner shadows if possible */
  @apply shadow-inner;
  box-shadow: inset 0 -2px 4px rgba(0, 0, 0, 0.1);
}
</style>
